import { useState } from 'react';
import { searchHsn } from '@coreledger/shared-types';
import { calcItemTotals, recalcInvoiceTotals } from '@coreledger/business-logic';
import type { InvoiceItem, Product } from '@coreledger/shared-types';

const GST_RATES = [0, 0.1, 0.25, 1, 1.5, 3, 5, 6, 7.5, 12, 18, 28];
const fmt2 = (n: number) => n.toFixed(2);

function uid() { return Math.random().toString(36).slice(2); }

function emptyItem(): InvoiceItem {
  return { id: uid(), description: '', qty: 1, rate: 0, taxableValue: 0, gstRate: 18, total: 0 };
}

function calcItem(it: InvoiceItem): InvoiceItem {
  const { taxableValue, total } = calcItemTotals(it);
  return { ...it, taxableValue, total };
}

interface Totals {
  subtotal: number;
  taxTotal: number;
  tdsAmt: number;
  tcsAmt: number;
  advanceAmt: number;
  netTotal: number;
  tdsRate?: number;
  tdsSection?: string;
  tcsRate?: number;
  isCgst: boolean;
}

interface Props {
  items: InvoiceItem[];
  products: Product[];
  totals: Totals;
  onItemsChange: (items: InvoiceItem[]) => void;
  onOpenProductPicker: () => void;
}

export function InvoiceItemsTable({ items, products, totals, onItemsChange, onOpenProductPicker }: Props) {
  const [hsnActive, setHsnActive] = useState<{ idx: number; query: string } | null>(null);
  const [prodDropOpenIdx, setProdDropOpenIdx] = useState<number | null>(null);

  const hsnResults = hsnActive && hsnActive.query.length >= 2 ? searchHsn(hsnActive.query).slice(0, 8) : [];

  function setItem(idx: number, k: keyof InvoiceItem, val: string | number) {
    const next = [...items];
    const updated = calcItem({ ...next[idx], [k]: typeof val === 'string' && k !== 'description' && k !== 'hsnSac' && k !== 'notes' ? parseFloat(val as string) || 0 : val });
    next[idx] = updated;
    onItemsChange(next);
  }

  function fillFromProduct(idx: number, p: Product) {
    const next = [...items];
    next[idx] = calcItem({ ...next[idx], productId: p.id, description: p.name, hsnSac: p.hsnSac, rate: p.rate, gstRate: p.gstRate });
    onItemsChange(next);
  }

  function addItem() {
    onItemsChange([...items, emptyItem()]);
  }

  function removeItem(idx: number) {
    onItemsChange(items.filter((_, i) => i !== idx));
  }

  const { subtotal, taxTotal, tdsAmt, tcsAmt, advanceAmt, netTotal, isCgst } = totals;

  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <div className="card-header">
        <span style={{ fontWeight: 700 }}>Line Items</span>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn btn-ghost btn-sm" onClick={onOpenProductPicker}>📦 Add Product</button>
          <button className="btn btn-ghost btn-sm" onClick={addItem}>+ Add Row</button>
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th>Description</th>
              <th style={{ width: 80 }}>HSN/SAC</th>
              <th style={{ width: 80 }}>Qty</th>
              <th style={{ width: 100 }}>Rate</th>
              <th style={{ width: 100 }}>Taxable</th>
              <th style={{ width: 90 }}>GST %</th>
              {isCgst ? <><th style={{ width: 90 }}>CGST</th><th style={{ width: 90 }}>SGST</th></> : <th style={{ width: 100 }}>IGST</th>}
              <th style={{ width: 110 }}>Total</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => {
              const gstAmt = item.taxableValue * item.gstRate / 100;
              const halfGst = gstAmt / 2;
              return (
                <tr key={item.id}>
                  <td style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{idx + 1}</td>
                  <td style={{ position: 'relative' }}>
                    <input
                      value={item.description}
                      onChange={e => {
                        const val = e.target.value;
                        const match = products.find(p => p.name.toLowerCase() === val.toLowerCase());
                        if (match) { fillFromProduct(idx, match); setProdDropOpenIdx(null); }
                        else { setItem(idx, 'description', val); setProdDropOpenIdx(idx); }
                      }}
                      onFocus={() => setProdDropOpenIdx(idx)}
                      onBlur={() => setTimeout(() => setProdDropOpenIdx(pi => pi === idx ? null : pi), 300)}
                      placeholder="Item description" autoComplete="off"
                    />
                    {prodDropOpenIdx === idx && (
                      <div
                        style={{ position: 'fixed', background: 'var(--surface)', border: '2px solid var(--primary)', borderRadius: 8, zIndex: 9999, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', minWidth: 300, maxHeight: 260, overflowY: 'auto' }}
                        ref={el => {
                          if (el) {
                            const input = el.previousElementSibling as HTMLElement;
                            if (input) {
                              const rect = input.getBoundingClientRect();
                              // Clamp so the dropdown (minWidth 300) never gets
                              // pushed past the right edge on a narrow screen —
                              // without this it silently clipped/became
                              // unreachable on phones since position:fixed
                              // elements don't force the page to scroll to reach them.
                              const left = Math.max(8, Math.min(rect.left, window.innerWidth - 308));
                              el.style.top = (rect.bottom + 4) + 'px'; el.style.left = left + 'px';
                            }
                          }
                        }}>
                        {products.filter(p => !item.description || p.name.toLowerCase().includes(item.description.toLowerCase())).slice(0, 10).map(p => (
                          <div key={p.id} onMouseDown={() => { fillFromProduct(idx, p); setProdDropOpenIdx(null); }}
                            style={{ padding: '0.6rem 1rem', cursor: 'pointer', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}
                            onMouseEnter={e => (e.currentTarget.style.background = 'var(--primary-light)')} onMouseLeave={e => (e.currentTarget.style.background = 'var(--surface)')}>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text)' }}>{p.name}</div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
                                {p.hsnSac ? `HSN: ${p.hsnSac}` : ''}{p.hsnSac && p.unit ? ' · ' : ''}{p.unit ?? ''}
                              </div>
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                              <div style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '0.9rem' }}>₹{p.rate}</div>
                              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>GST {p.gstRate}%</div>
                            </div>
                          </div>
                        ))}
                        {products.filter(p => !item.description || p.name.toLowerCase().includes(item.description.toLowerCase())).length === 0 && (
                          <div style={{ padding: '0.75rem 1rem', color: 'var(--text-muted)', fontSize: '0.85rem' }}>No products found. Use "Add Product" to add products.</div>
                        )}
                      </div>
                    )}
                  </td>
                  <td style={{ position: 'relative' }}>
                    <input value={item.hsnSac ?? ''}
                      onChange={e => { setItem(idx, 'hsnSac', e.target.value); setHsnActive({ idx, query: e.target.value }); }}
                      onFocus={e => setHsnActive({ idx, query: e.target.value })}
                      onBlur={() => setTimeout(() => setHsnActive(null), 150)}
                      placeholder="HSN" style={{ width: 70 }} />
                    {hsnActive?.idx === idx && hsnResults.length > 0 && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, minWidth: 280, maxHeight: 200, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }}>
                        {hsnResults.map(h => (
                          <div key={h.code} onMouseDown={() => {
                            setItem(idx, 'hsnSac', h.code);
                            if (h.defaultGstRate !== undefined) setItem(idx, 'gstRate', h.defaultGstRate);
                            setHsnActive(null);
                          }} style={{ padding: '0.45rem 0.65rem', cursor: 'pointer', borderBottom: '1px solid var(--border)', display: 'flex', gap: '0.5rem', alignItems: 'center' }}
                          onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-alt)')} onMouseLeave={e => (e.currentTarget.style.background = 'var(--surface)')}>
                            <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.8rem', color: 'var(--primary)', minWidth: 70 }}>{h.code}</span>
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{h.description}</span>
                            {h.defaultGstRate !== undefined && <span style={{ fontSize: '0.7rem', background: 'var(--primary-light)', color: 'var(--primary)', borderRadius: 4, padding: '0 4px', flexShrink: 0 }}>{h.defaultGstRate}%</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td><input type="number" value={item.qty} onChange={e => setItem(idx, 'qty', e.target.value)} min={0} style={{ width: 70 }} /></td>
                  <td><input type="number" value={item.rate} onChange={e => setItem(idx, 'rate', e.target.value)} min={0} style={{ width: 90 }} /></td>
                  <td style={{ textAlign: 'right', fontWeight: 500 }}>₹{fmt2(item.taxableValue)}</td>
                  <td>
                    <select value={item.gstRate} onChange={e => setItem(idx, 'gstRate', e.target.value)} style={{ width: 70 }}>
                      {GST_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
                    </select>
                  </td>
                  {isCgst
                    ? <><td style={{ textAlign: 'right' }}>₹{fmt2(halfGst)}</td><td style={{ textAlign: 'right' }}>₹{fmt2(halfGst)}</td></>
                    : <td style={{ textAlign: 'right' }}>₹{fmt2(gstAmt)}</td>}
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>₹{fmt2(item.total)}</td>
                  <td>
                    {items.length > 1 && (
                      <button onClick={() => removeItem(idx)} style={{ background: 'none', border: 'none', color: 'var(--danger)', fontSize: '1rem', padding: '0 0.25rem', cursor: 'pointer' }}>×</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '1rem 1.25rem' }}>
        <table style={{ width: 320 }}>
          <tbody>
            <tr><td style={{ color: 'var(--text-2)', padding: '0.2rem 0' }}>Subtotal (taxable)</td><td style={{ textAlign: 'right', fontWeight: 500 }}>₹{fmt2(subtotal)}</td></tr>
            {isCgst
              ? <><tr><td style={{ color: 'var(--text-2)', padding: '0.2rem 0' }}>CGST</td><td style={{ textAlign: 'right' }}>₹{fmt2(taxTotal / 2)}</td></tr>
                   <tr><td style={{ color: 'var(--text-2)', padding: '0.2rem 0' }}>SGST</td><td style={{ textAlign: 'right' }}>₹{fmt2(taxTotal / 2)}</td></tr></>
              : <tr><td style={{ color: 'var(--text-2)', padding: '0.2rem 0' }}>IGST</td><td style={{ textAlign: 'right' }}>₹{fmt2(taxTotal)}</td></tr>}
            {tdsAmt > 0 && <tr><td style={{ color: 'var(--danger)', padding: '0.2rem 0' }}>TDS ({totals.tdsRate}%{totals.tdsSection ? ` — ${totals.tdsSection}` : ''})</td><td style={{ textAlign: 'right', color: 'var(--danger)' }}>− ₹{fmt2(tdsAmt)}</td></tr>}
            {tcsAmt > 0 && <tr><td style={{ color: 'var(--text-2)', padding: '0.2rem 0' }}>TCS ({totals.tcsRate}%)</td><td style={{ textAlign: 'right' }}>₹{fmt2(tcsAmt)}</td></tr>}
            {advanceAmt > 0 && <tr><td style={{ color: 'var(--danger)', padding: '0.2rem 0' }}>Advance Received</td><td style={{ textAlign: 'right', color: 'var(--danger)' }}>− ₹{fmt2(advanceAmt)}</td></tr>}
            <tr style={{ borderTop: '2px solid var(--border)' }}>
              <td style={{ fontWeight: 700, padding: '0.4rem 0 0' }}>Net Total</td>
              <td style={{ textAlign: 'right', fontWeight: 800, fontSize: '1.1rem', paddingTop: '0.4rem', color: 'var(--primary)' }}>₹{fmt2(netTotal - advanceAmt)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export { emptyItem, recalcInvoiceTotals as recalcTotals };
